import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inboxFirestore } from '../../services/firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useSelector } from 'react-redux';
import { RootState } from '..';
import { v4 as uuidv4 } from 'uuid';

export interface TicketCommentData {
	id?: string;
	date?: number;
	user: { uid: string; name: string };
	content: string;
	public?: boolean;
	emailViewDetectorUid?: string;
}

export interface CommentsState {
	comments: Record<string, TicketCommentData[]>;
	loading: boolean;
	error: string | null;
}

const initialState: CommentsState = {
	comments: {},
	loading: false,
	error: null,
};

// Add comments to a ticket
export const addCommentToTicket = createAsyncThunk(
	'comments/addCommentToTicket',
	async (
		{ ticketId, commentData }: { ticketId: string; commentData: TicketCommentData },
		thunkApi
	) => {
		try {
			const commentsRef = doc(inboxFirestore, 'comments', ticketId);
			const commentsSnap = await getDoc(commentsRef);

			const existingComments = commentsSnap.exists() ? commentsSnap.data().comments ?? [] : [];

			const newComment = {
				id: uuidv4(),
				date: Timestamp.now().toMillis(),
				public: true,
				...commentData,
			};

			const updatedComments = [...existingComments, newComment];

			await setDoc(commentsRef, { ticketId, comments: updatedComments });

			return { ticketId, commentId: newComment.id, comments: updatedComments };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const getCommentByTicketId = createAsyncThunk(
	'comments/getCommentByTicketId',
	async (ticketId: string, thunkApi) => {
		try {
			const commentsRef = doc(inboxFirestore, 'comments', ticketId);
			const commentsSnap = await getDoc(commentsRef);

			if (commentsSnap.exists()) {
				return {
					ticketId,
					comments: commentsSnap.data().comments ?? [],
				};
			}

			return { ticketId, comments: [] };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const commentsSlice = createSlice({
	name: 'comments',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(addCommentToTicket.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(addCommentToTicket.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.comments[action.payload.ticketId] = action.payload.comments;
			})
			.addCase(addCommentToTicket.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(getCommentByTicketId.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getCommentByTicketId.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.comments[action.payload.ticketId] = action.payload.comments;
			});
	},
});

export const useComments = () => useSelector<RootState, CommentsState>((state) => state.comments);
export default commentsSlice.reducer;
